import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

interface DocumentContextType {
  pdf: any;
  numPages: number;
  onLoadSuccess?: (data: { numPages: number }) => void;
  onLoadError?: (error: Error) => void;
}

const DocumentContext = createContext<DocumentContextType | null>(null);

export const useDocument = () => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error('Page must be used within a Document');
  }
  return context;
};

interface DocumentProps {
  file: string;
  onLoadSuccess?: (data: { numPages: number }) => void;
  onLoadError?: (error: Error) => void;
  options?: {
    cMapUrl?: string;
    cMapPacked?: boolean;
  };
  children: React.ReactNode;
}

export const Document: React.FC<DocumentProps> = ({
  file,
  onLoadSuccess,
  onLoadError,
  options = {},
  children,
}) => {
  const [pdf, setPdf] = useState<any>(null);
  const [numPages, setNumPages] = useState(0);
  const loadingTaskRef = useRef<any>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const loadPdf = async () => {
      try {
        if (!window.pdfjsLib) {
          throw new Error('PDF.js library is not loaded');
        }

        // Cancel previous loading task if exists
        if (loadingTaskRef.current) {
          try {
            loadingTaskRef.current.destroy();
          } catch (e) {
            // Ignore errors during cleanup
          }
          loadingTaskRef.current = null;
        }

        // Extract base64 data from data URL
        const base64Data = file.replace(/^data:application\/pdf;base64,/, '');

        const loadingTask = window.pdfjsLib.getDocument({
          data: atob(base64Data),
          ...options,
        });

        loadingTaskRef.current = loadingTask;

        const pdfDocument = await loadingTask.promise;

        // Check if component is still mounted before setting state
        if (!isMountedRef.current) {
          return;
        }

        setPdf(pdfDocument);
        setNumPages(pdfDocument.numPages);

        if (onLoadSuccess) {
          onLoadSuccess({ numPages: pdfDocument.numPages });
        }
      } catch (error) {
        // Check if component is still mounted before handling error
        if (!isMountedRef.current) {
          return;
        }

        // Don't log errors for cancelled operations
        if ((error as any)?.name !== 'AbortException') {
          console.error('Error loading PDF:', error);
          if (onLoadError) {
            onLoadError(error as Error);
          }
        }
      }
    };

    if (file) {
      loadPdf();
    }

    return () => {
      if (loadingTaskRef.current) {
        try {
          loadingTaskRef.current.destroy();
        } catch (e) {
          // Ignore errors during cleanup
        }
        loadingTaskRef.current = null;
      }
    };
  }, [file, onLoadSuccess, onLoadError, options]);

  const contextValue: DocumentContextType = {
    pdf,
    numPages,
    onLoadSuccess,
    onLoadError,
  };

  return (
    <DocumentContext.Provider value={contextValue}>
      {children}
    </DocumentContext.Provider>
  );
};
